/**
 * IT Command Center — functional dashboard for Information Technology.
 *
 * System status (backend health), active integrations, IT authorization
 * requests, and cross-department IT inbox. Turns IT from a placeholder
 * page into a working cockpit.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Cpu, Zap, Shield, Globe, ArrowRightLeft, CheckCircle2, AlertTriangle, Radio } from 'lucide-react';
import { KpiTile, DeptInbox, RouteTaskDialog } from './DeptTaskShared';
import UnifiedGeoRoleFilter from '../UnifiedGeoRoleFilter';
import { DepartmentDetails } from './DepartmentDetails';
import { DepartmentSectionMenu } from './DepartmentNavigation';
import { DEPT_BY_KEY } from './AdminDepartments';

const API = process.env.REACT_APP_BACKEND_URL;

/** Third-party integrations to health-check. Kept declarative so
 *  toggling a new integration on/off is a one-line addition. */
const INTEGRATIONS = [
  { key: 'gemini', label: 'Gemini Vision 2.5 Flash', purpose: 'AI violation analysis', status: 'live' },
  { key: 'emergent', label: 'Emergent LLM Key', purpose: 'Universal LLM access', status: 'live' },
  { key: 'twilio', label: 'Twilio SMS', purpose: 'Outbound SMS · (817) 672-8833', status: 'awaiting-keys' },
  { key: 'resend', label: 'Resend Email', purpose: 'Transactional email · admin@nobleempirecs.com', status: 'awaiting-keys' },
  { key: 'ringcentral', label: 'RingCentral', purpose: 'Voice · (888) 241-3077', status: 'awaiting-keys' },
  { key: 'stripe', label: 'Stripe', purpose: 'Payments · Elements + ACH', status: 'awaiting-keys' },
  { key: 'quickbooks_online', label: 'QuickBooks Online', purpose: 'Accounting sync', status: 'awaiting-keys' },
  { key: 'nominatim', label: 'Nominatim Geocoder', purpose: 'Property geocoding', status: 'live' },
  { key: 'mongo', label: 'MongoDB', purpose: 'Primary datastore', status: 'live' },
];

const STATUS_TONE = {
  live: 'bg-emerald-600 text-white',
  'awaiting-keys': 'bg-amber-500 text-black',
  degraded: 'bg-red-600 text-white',
};

export default function ITCommandCenter({ boardFilter }) {
  const [health, setHealth] = useState({ status: 'checking' });
  const [activeUsers, setActiveUsers] = useState(0);
  const [route, setRoute] = useState(false);
  const [liveSnapshot, setLiveSnapshot] = useState(null);

  useEffect(() => {
    (async () => {
      // Health probe uses `/api/auth/me` since it's guaranteed to be
      // authenticated and cheap — a 200 here means auth stack + Mongo +
      // route registry are all healthy.
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        setHealth(res.ok ? { status: 'live' } : { status: 'degraded' });
      } catch (_) { setHealth({ status: 'degraded' }); }
      try {
        // Section 2 (iter-138) — append pivot filter queryString so
        // active-user count reflects the selected scope.
        const qs = boardFilter?.queryString ? `?${boardFilter.queryString}` : '';
        const res = await fetch(`${API}/api/company/employees${qs}`, { credentials: 'include' });
        if (res.ok) {
          const d = await res.json();
          setActiveUsers(Array.isArray(d) ? d.length : 0);
        }
      } catch (_) { /* noop */ }
      // Iter-182 addendum — live integrations snapshot (Resend sender,
      // Twilio/RingCentral numbers, and connection status per key).
      try {
        const res = await fetch(`${API}/api/it/integrations`, { credentials: 'include' });
        if (res.ok) setLiveSnapshot(await res.json());
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.queryString, boardFilter?.refreshTick]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="it-command-center">
      {/* Header */}
      <div
        className="rounded-2xl p-4 sm:p-6 border-2 flex flex-wrap items-start gap-4"
        style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 60%, #2D2D2D 100%)', borderColor: 'rgba(14,165,233,0.5)' }}
        data-testid="it-hero"
      >
        <div className="h-16 w-16 rounded-xl flex items-center justify-center"
             style={{ backgroundColor: 'rgba(14,165,233,0.15)', border: '2px solid #0ea5e9' }}>
          <Cpu className="h-8 w-8 text-[#0ea5e9]" />
        </div>
        <div className="flex-1 min-w-0 basis-48">
          <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#0ea5e9]">
            Information Technology · Command Center · TEC
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-1"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.02em' }}>
            IT OPERATIONS
          </h1>
          <p className="text-xs text-zinc-400 mt-3 max-w-3xl leading-relaxed">
            Keep every system, device, and integration online — securely. Live system health,
            integration status, access authorizations, and cross-department IT tickets from
            Operations flow through this cockpit.
          </p>
        </div>
        <Button onClick={() => setRoute(true)} className="bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-bold"
                data-testid="it-route-btn">
          <ArrowRightLeft className="h-4 w-4 mr-2" /> Route Ticket
        </Button>
      </div>

      {/* Big City + Admin/Field slicer — CEO's platform-wide filter */}

      {/* Iter-111 · Enterprise Pivot Filter — below header + slicer. */}
      <DepartmentDetails dept={DEPT_BY_KEY.it} prefix="it" accent="#0ea5e9" />
      {boardFilter && <UnifiedGeoRoleFilter filter={boardFilter} testIdPrefix="unified-filter-it" />}

      {/* System health */}
      <section data-testid="it-system-health">
        <h2 className="text-xs uppercase tracking-widest text-[#0ea5e9] mb-3 font-semibold">System Health</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile icon={<Zap className="h-5 w-5" />} label="Backend API"
                   value={health.status === 'live' ? 'ONLINE' : health.status.toUpperCase()}
                   sub={health.status === 'live' ? 'All routes reachable' : 'Investigate'}
                   tone={health.status === 'live' ? '#22c55e' : '#ef4444'}
                   testid="it-kpi-backend-health" />
          <KpiTile icon={<Radio className="h-5 w-5" />} label="Active users" value={activeUsers}
                   sub="Loaded from NECS company roster" tone="#0ea5e9" testid="it-kpi-active-users" />
          <KpiTile icon={<Globe className="h-5 w-5" />} label="Integrations · live"
                   value={INTEGRATIONS.filter((i) => i.status === 'live').length}
                   sub={`of ${INTEGRATIONS.length} configured`}
                   tone="#22c55e" testid="it-kpi-live-integrations" />
          <KpiTile icon={<AlertTriangle className="h-5 w-5" />} label="Integrations · awaiting"
                   value={INTEGRATIONS.filter((i) => i.status === 'awaiting-keys').length}
                   sub="Need API keys from CEO/Ops"
                   tone="#f59e0b" testid="it-kpi-awaiting-integrations" />
        </div>
      </section>

      {/* Integrations panel */}
      <DepartmentSectionMenu prefix="it" sections={[{key:'it-system-health',label:'System Health'},{key:'it-integrations-panel',label:'Integrations'},{key:'it-department-inbox',label:'IT Ticket Inbox'}]}/>
      <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="it-integrations-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-white flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Globe className="h-5 w-5 text-[#0ea5e9]" /> THIRD-PARTY INTEGRATIONS
          </h3>
        </div>
        <ul className="space-y-2">
          {INTEGRATIONS.map((i) => {
            // Iter-182 addendum — override baked-in status with the live
            // snapshot when available so ops see the true state.
            const liveKey = i.key === 'quickbooks_online' ? 'quickbooks_online' : i.key;
            const live = liveSnapshot?.[liveKey];
            const effective = live?.status === 'connected' ? 'live'
              : live?.status === 'pending_keys' ? 'awaiting-keys'
              : i.status;
            return (
              <li key={i.key} className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 flex items-center justify-between"
                  data-testid={`it-integration-${i.key}`}>
                <div>
                  <p className="text-sm font-bold text-white">{i.label}</p>
                  <p className="text-[11px] text-zinc-400">{i.purpose}</p>
                </div>
                <Badge className={STATUS_TONE[effective] || 'bg-zinc-600'}
                        data-testid={`it-integration-${i.key}-status`}>
                  {effective === 'live' && <CheckCircle2 className="h-3 w-3 mr-1 inline" />}
                  {effective.toUpperCase().replace('-', ' ')}
                </Badge>
              </li>
            );
          })}
        </ul>
      </Card>

      {/* IT dept inbox */}
      <div data-testid="it-department-inbox"><DeptInbox deptKey="it" title="IT TICKET INBOX" /></div>

      <RouteTaskDialog open={route} onClose={() => setRoute(false)} onSaved={() => window.location.reload()} defaultDept="it" />
    </div>
  );
}
